/**
 * `<CanvasGestureLegend/>` — a quiet, dismissible "how to drive the canvas"
 * hint (#931, the small bit).
 *
 * The Map canvas has several gestures that aren't all self-evident: zoom
 * (Ctrl/⌘+wheel or the ± control), pan (drag the background), collapse/expand
 * (the ▶ chip on a node), and — when the dependency layer is on — link (drag
 * the → connector port, or "link →" then click a target). This is a small "?"
 * affordance in the canvas corner that pops a calm legend; it is opt-IN (not a
 * coachmark that nags) and its DISMISS persists for the session (sessionStorage),
 * so a lead who's seen it isn't pestered again until a fresh session.
 *
 * Presentational + self-contained: it owns only its open/dismissed UI state.
 * The `dependenciesOn` flag toggles the link row (the only gesture that's
 * conditional). No data, no gating logic beyond that flag.
 */

import { useState } from "react";

import { PALETTE } from "../tree/palette";

/** Session-scoped dismiss key — survives re-mounts within a tab, not across tabs/sessions. */
const DISMISS_KEY = "tarii.taskmap.gestureLegend.dismissed";

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    // Private mode / storage disabled — treat as not-dismissed (show the calm "?").
    return false;
  }
}

function persistDismissed(): void {
  try {
    sessionStorage.setItem(DISMISS_KEY, "1");
  } catch {
    // Best-effort only; a failed write just means it'll show again next mount.
  }
}

export interface CanvasGestureLegendProps {
  /** When the dependency layer is on, include the "link" gesture row (#766). */
  dependenciesOn: boolean;
}

export function CanvasGestureLegend({ dependenciesOn }: CanvasGestureLegendProps) {
  // Dismissed-for-the-session hides the "?" entirely; otherwise it sits closed.
  const [dismissed, setDismissed] = useState<boolean>(readDismissed);
  const [open, setOpen] = useState(false);

  if (dismissed) return null;

  const rows: { gesture: string; how: string }[] = [
    { gesture: "Zoom", how: "Ctrl / ⌘ + wheel, or the ± control" },
    { gesture: "Pan", how: "drag the canvas background" },
    { gesture: "Collapse / expand", how: "the ▶ chip on a node" },
  ];
  if (dependenciesOn) {
    rows.push({
      gesture: "Link",
      how: "drag the → port, or “link →” then click a target",
    });
  }

  if (!open) {
    return (
      <button
        type="button"
        data-testid="gesture-legend-toggle"
        aria-label="How to use the canvas"
        title="How to use the canvas"
        onClick={() => setOpen(true)}
        className="flex h-6 w-6 items-center justify-center rounded-full border text-xs font-semibold shadow-sm transition-colors hover:bg-slate-50"
        style={{
          borderColor: PALETTE.softBorder,
          backgroundColor: "#FFFFFF",
          color: PALETTE.slate,
        }}
      >
        ?
      </button>
    );
  }

  return (
    <div
      data-testid="gesture-legend"
      className="w-64 rounded-lg border bg-white p-3 shadow-lg"
      style={{ borderColor: PALETTE.softBorder, color: PALETTE.ink }}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold" style={{ color: PALETTE.slate }}>
          Getting around the canvas
        </span>
        <button
          type="button"
          data-testid="gesture-legend-close"
          aria-label="Close"
          onClick={() => setOpen(false)}
          className="-mr-1 rounded p-0.5 text-sm leading-none hover:bg-slate-50"
          style={{ color: PALETTE.faint }}
        >
          ✕
        </button>
      </div>
      <dl className="mt-2 space-y-1.5">
        {rows.map((row) => (
          <div key={row.gesture} className="flex items-baseline gap-2 text-[11px]">
            <dt className="w-28 shrink-0 font-medium" style={{ color: PALETTE.ink }}>
              {row.gesture}
            </dt>
            <dd style={{ color: PALETTE.muted }}>{row.how}</dd>
          </div>
        ))}
      </dl>
      <button
        type="button"
        data-testid="gesture-legend-dismiss"
        onClick={() => {
          persistDismissed();
          setDismissed(true);
        }}
        className="mt-2.5 text-[11px] font-medium transition-colors hover:underline"
        style={{ color: PALETTE.teal }}
      >
        Got it — don’t show again
      </button>
    </div>
  );
}
